// © 2026 AttainIP Engineering
import {
  MessageBubble,
  Waypoints,
  parseAssistantContent,
  type ChatMessage,
} from '@attain/ui-lib/components/chat';

// Permissive submit-token pattern — accepts nested parens inside the
// message (the ss-lib regex uses `[^)]*?` which silently fails on
// real-world messages like `(using the @L9-* chart lines)`) and brackets
// inside the label (lazy `[\s\S]*?` terminated by the first `](`, so footnote
// refs like `[34]` in the label don't break the match).
const SUBMIT_RE = /::submit\[([\s\S]*?)\]\([\s\S]*?\)::/g;

export function extractWaypoints(text: string): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  SUBMIT_RE.lastIndex = 0;
  while ((m = SUBMIT_RE.exec(text)) !== null) {
    out.push(m[1]);
  }
  return out;
}

export function stripSubmitTokens(text: string): string {
  return text.replace(/::submit\[[^\]]+?\]\([\s\S]*?\)::/g, '').trim();
}

/**
 * Renders a single message from a chat transcript using the same
 * pipeline as the user-facing SS UI: MessageBubble for prose + inline
 * tokens, Waypoints for suggested follow-ups, and a "Thought for Ns"
 * details toggle for the per-turn agent reasoning trace.
 *
 * Designed for read-only contexts — Waypoints are rendered disabled.
 */
export function TranscriptMessage({ raw, index }: { raw: any; index: number }) {
  const role: 'user' | 'assistant' | 'tool_call' | 'tool_result' =
    raw.role === 'assistant' || raw.role === 'user' || raw.role === 'tool_call' || raw.role === 'tool_result'
      ? raw.role
      : raw.role === 'tool'
      ? 'tool_result'
      : 'assistant';
  const rawContent = typeof raw.content === 'string'
    ? raw.content
    : raw.content == null
    ? ''
    : JSON.stringify(raw.content, null, 2);
  const ts = raw.created_at || raw.timestamp;

  const parsed = role === 'assistant'
    ? parseAssistantContent(rawContent)
    : { content: rawContent, thought: null };
  const waypoints = role === 'assistant' ? extractWaypoints(parsed.content) : [];
  const bubbleContent = role === 'assistant'
    ? stripSubmitTokens(parsed.content)
    : parsed.content;

  const message: ChatMessage = {
    id: raw.id ?? `m-${index}`,
    role,
    content: bubbleContent,
    timestamp: ts ?? new Date().toISOString(),
    waypoints: waypoints.length > 0 ? waypoints : undefined,
    isFinal: true,
  };

  return (
    <div className="border-b last:border-0 pb-3">
      <div className="flex items-center gap-2 mb-1 text-xs">
        <span
          className={`px-2 py-0.5 rounded ${
            role === 'user'
              ? 'bg-blue-100 text-blue-700'
              : role === 'assistant'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-amber-100 text-amber-700'
          }`}
        >
          {role}
        </span>
        {ts && <span className="text-gray-400">{new Date(ts).toLocaleString()}</span>}
        {parsed.thought && (
          <details className="ml-auto">
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
              Thought for {parsed.thought.seconds}s
            </summary>
            <div className="mt-2 ml-4 space-y-1 text-xs text-gray-600">
              {parsed.thought.trace.map((t, ti) =>
                t.kind === 'tool' ? (
                  <div key={ti}>
                    <span className="font-mono text-amber-700">
                      🛠 {t.name}
                      {t.subject ? ` (${t.subject})` : ''}
                    </span>
                  </div>
                ) : (
                  <div key={ti} className="italic text-gray-500">
                    "{t.text}"
                  </div>
                ),
              )}
              {parsed.thought.trace.length === 0 && (
                <span className="italic text-gray-400">no recorded trace</span>
              )}
            </div>
          </details>
        )}
      </div>
      <MessageBubble message={message} />
      {waypoints.length > 0 && (
        <Waypoints
          waypoints={waypoints}
          onSelect={() => { /* read-only */ }}
          disabled
        />
      )}
    </div>
  );
}
